import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { tierListMonthly } from '../config/pricingPlans';
import { HOME_META } from '../lib/siteMeta';
import * as PERKS from '../content/perks';
import * as PREP from '../content/prep';

// Read from config rather than typed into the sentence. This description said
// "starting at $29/month" while the cheapest paid plan was $39, which is a price
// nobody could be charged, sitting in the snippet Google shows for the pricing
// page. Deriving it means the next price change carries it along.
const CHEAPEST_PAID_MONTHLY = tierListMonthly.basic;

const SEO_DATA = {
  // The same title and description the prerendered homepage ships, from one source.
  '/': {
    title: HOME_META.title,
    description: HOME_META.description,
    keywords: 'CRM for wedding photographers, CRM for videographers, wedding photography software, photography CRM, best CRM photographers 2026',
  },
  '/preview': {
    title: 'Pixally CRM | The CRM for Wedding Photographers & Videographers',
    description: 'An all-in-one CRM for wedding photographers and videographers, with photo, video and hybrid gallery delivery built in. Manage leads, contracts, contractors and post-production.',
    keywords: 'CRM for wedding photographers, CRM for videographers, wedding photography software',
  },
  '/preview/pricing': {
    title: 'Pricing | Pixally CRM for Wedding Photographers & Videographers',
    description: `Simple, transparent pricing for Pixally CRM. Plans starting at $${CHEAPEST_PAID_MONTHLY}/month. Includes contractor management, post-production workflows, and client portal. Free trial available.`,
    keywords: 'Pixally pricing, wedding photographer CRM cost, photography software pricing, wedding CRM pricing',
  },
  '/preview/about': {
    title: 'About Pixally | Built by Wedding Photographers, for Wedding Photographers',
    description: 'Pixally was created by wedding photographers who were frustrated with generic CRMs. Learn our story and why we built a CRM for wedding creatives.',
    keywords: 'about Pixally, wedding photographer software, photography CRM company',
  },
  '/preview/features/contractors': {
    title: 'Contractor & Second Shooter Management | Pixally CRM',
    description: 'Manage second shooters, associate photographers, and contractors in one place. Track availability, payments, and assignments, with a portal for every contractor.',
    keywords: 'second shooter management, contractor management photographers, manage photography team, associate photographer scheduling',
  },
  '/preview/features/post-production': {
    title: 'Post-Production Workflow Management | Pixally CRM',
    description: 'Automate your wedding photography post-production workflow. Tasks auto-generate based on package deliverables. Never miss a deadline or forget a delivery.',
    keywords: 'wedding photography workflow, post-production management, photo editing workflow, photography project management',
  },
  '/preview/features/leads-booking': {
    title: 'Lead & Booking Pipeline | Pixally CRM',
    description: 'Capture wedding photography leads, automate follow-ups, send proposals with e-signatures, and collect retainers. Close more bookings with smart venue recognition.',
    keywords: 'wedding photography leads, booking pipeline CRM, photography proposal software, wedding photographer booking system',
  },
  '/preview/features/client-portal': {
    title: 'Client Portal for Wedding Photographers | Pixally CRM',
    description: 'Give your wedding clients a branded portal to sign contracts, pay invoices, fill questionnaires, and access galleries. Professional client experience built-in.',
    keywords: 'client portal photographers, wedding photography client management, photography client experience',
  },
  '/blog': {
    title: 'Blog | Pixally CRM - Tips for Wedding Photographers & Videographers',
    description: 'Expert tips, guides, and insights for wedding photographers and videographers. Learn how to grow your business, manage clients, and streamline your workflow.',
    keywords: 'wedding photography tips, videographer business advice, photography business blog',
  },
  // Legal pages are indexable on purpose (see TermsPage.jsx). Without entries here
  // they fell through to the '/preview' default and shipped with the homepage title
  // and description on two pages that are listed in sitemap.xml.
  '/terms': {
    title: 'Terms of Service | Pixally',
    description: 'The terms governing use of Pixally, covering the pre-launch waitlist, participant portal, subscriptions, billing, and acceptable use of the platform.',
    keywords: 'Pixally terms of service, Pixally terms, wedding CRM terms of service',
  },
  '/privacy': {
    title: 'Privacy Policy | Pixally',
    description: 'How Pixally collects, uses and protects your data, covering cookies, analytics, automated assessment, international transfers, and your privacy rights.',
    keywords: 'Pixally privacy policy, wedding CRM privacy, photographer CRM data protection',
  },
  // Reached by couples and wedding guests from branded portals and gallery
  // links, so the title has to say whose page this is: they have often never
  // heard of Pixally when they land here.
  '/client-terms': {
    title: 'Client Terms of Use | Pixally',
    description: 'Terms for clients and guests using a client portal, gallery, or share link powered by Pixally. Your photos, your access, and who to contact about your booking.',
    keywords: 'Pixally client terms, wedding gallery terms, client portal terms of use',
  },
  '/client-privacy': {
    title: 'Client Privacy Notice | Pixally',
    description: 'What happens to your information when you use a client portal or gallery powered by Pixally: who is responsible, what is held, who can see it, and how to exercise your rights.',
    keywords: 'Pixally client privacy, wedding gallery privacy, client portal privacy notice',
  },
  // From content/perks.js, the same title and description the prerendered page ships.
  '/perks': {
    title: PERKS.SEO_TITLE,
    description: PERKS.SEO_DESCRIPTION,
    keywords: 'Pixally perks, discounts for wedding photographers, discounts for wedding videographers, wedding filmmaker tools',
  },
  // From content/prep.js, the same title and description the prerendered page ships.
  '/prep': {
    title: PREP.SEO_TITLE,
    description: PREP.SEO_DESCRIPTION,
    keywords: 'Pixally Prep, wedding video editing prep, sync wedding footage, transcribe wedding video, Mac app for wedding filmmakers, Windows app for wedding filmmakers',
  },
  // The page Prep's ads link to: noindex, but it carries /prep's title and
  // description, which are what a link sent to a computer previews with.
  '/get-prep': {
    title: PREP.SEO_TITLE,
    description: PREP.SEO_DESCRIPTION,
    keywords: 'Pixally Prep, Mac app for wedding filmmakers, Windows app for wedding filmmakers',
  },
  // Comparison pages. Title and description match content/compare/*.js, which
  // the prerendered page uses.
  '/compare/honeybook': {
    title: 'Pixally vs HoneyBook for Wedding Photographers and Videographers',
    description: 'How Pixally and HoneyBook compare on price, payment fees, galleries, film delivery, second shooters and post-production, with every HoneyBook fact sourced.',
    keywords: 'Pixally vs HoneyBook, HoneyBook alternative for wedding photographers, HoneyBook alternative for videographers',
  },
  '/compare/dubsado': {
    title: 'Pixally vs Dubsado for Wedding Photographers and Videographers',
    description: 'How Pixally and Dubsado compare on price, payment fees, galleries, film delivery, team members and second shooters, with every Dubsado fact sourced.',
    keywords: 'Pixally vs Dubsado, Dubsado alternative for wedding photographers, Dubsado alternative for videographers',
  },
  '/compare/studio-ninja': {
    title: 'Pixally vs Studio Ninja for Wedding Photographers and Videographers',
    description: 'How Pixally and Studio Ninja compare on price, galleries, film delivery, team members, second shooters and post-production, with every Studio Ninja fact sourced.',
    keywords: 'Pixally vs Studio Ninja, Studio Ninja alternative, Studio Ninja vs HoneyBook, photography CRM',
  },
  '/compare/pixieset': {
    title: 'Pixally vs Pixieset for Wedding Photographers and Videographers',
    description: 'How Pixally and Pixieset compare on price, galleries, video, print sales and CRM tools for wedding photographers and videographers, with every Pixieset fact sourced.',
  },
  '/compare/pic-time': {
    title: 'Pixally vs Pic-Time for Wedding Photographers and Videographers',
    description: 'How Pixally and Pic-Time compare on price, galleries, video, print sales and CRM tools for wedding photographers and videographers, with every Pic-Time fact sourced.',
  },
  '/compare/shootproof': {
    title: 'Pixally vs ShootProof for Wedding Photographers and Videographers',
    description: 'How Pixally and ShootProof compare on price, galleries, video, print sales and CRM tools for wedding photographers and videographers, with every ShootProof fact sourced.',
  },
  '/compare/client-gallery-platforms': {
    title: 'Pixieset vs Pic-Time vs ShootProof for Wedding Photographers (2026)',
    description: 'Pixieset, Pic-Time and ShootProof compared for wedding photographers and videographers: price, photo storage, video, print sales and CRM tools, with Pixally alongside.',
  },
  '/compare/vsco-workspace': {
    title: 'Pixally vs VSCO Workspace (Táve) for Wedding Photographers',
    description: 'How Pixally and VSCO Workspace, formerly Táve, compare on price, galleries, film delivery, second shooters and post-production, with every VSCO fact sourced.',
  },
  '/compare/vidflow': {
    title: 'Pixally vs VidFlow for Wedding Film Delivery',
    description: 'How Pixally and VidFlow compare for delivering wedding films: upload packs, access, TV apps, photos alongside films, and running the studio, with every VidFlow fact sourced.',
  },
  '/faq': {
    title: 'Pixally FAQ: Plans, Pricing, Galleries, Film Delivery and Second Shooters',
    description: 'Answers about Pixally, the CRM for wedding photographers and videographers: what it is, when it launches, plans and fees, galleries and film delivery, second shooters and switching.',
  },
  '/about': {
    title: 'About Pixally: the CRM for Wedding Photographers and Videographers',
    description: 'Pixally is a CRM for wedding photographers and videographers, built by wedding filmmaker Ryan Geldermann and launching January 1, 2027.',
  },
  '/compare/honeybook-alternatives': {
    title: 'HoneyBook Alternatives for Wedding Photographers (2026)',
    description: 'Dubsado, Studio Ninja, Sprout Studio, Pixieset, VSCO Workspace and Pixally compared for wedding photographers: price, trials, client galleries and who each one suits.',
    keywords: 'HoneyBook alternatives, HoneyBook alternative for wedding photographers, Dubsado vs Studio Ninja, photography CRM',
  },
  '/compare/best-crm-for-wedding-videographers': {
    title: 'Best CRM for Wedding Videographers (2026)',
    description: 'HoneyBook, Dubsado, Studio Ninja, Plutio and Pixally compared for wedding videographers: film delivery, editing, second shooters and price, plus when you still need VidFlow.',
    keywords: 'best CRM for wedding videographers, wedding videography CRM, HoneyBook alternatives for videographers, VidFlow',
  },
  // Reached by second shooters and associates from the CRM's contractor sign-up.
  '/contractor-terms': {
    title: 'Contractor Terms of Use | Pixally',
    description: 'Terms for second shooters, associates, editors and other professionals with a Pixally contractor account: your account, studios, payments and tax forms.',
    keywords: 'Pixally contractor terms, second shooter terms of use, associate photographer terms',
  },
  '/contractor-privacy': {
    title: 'Contractor Privacy Notice | Pixally',
    description: 'How Pixally handles information about second shooters, associates and other professionals with a contractor account, including tax forms and payouts.',
    keywords: 'Pixally contractor privacy, second shooter privacy, W-9 privacy notice',
  },
};

const SEOHead = () => {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname;
    const seoData = SEO_DATA[path] || SEO_DATA['/preview'];
    
    // Update title
    document.title = seoData.title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seoData.description);
    }
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', seoData.keywords);
    }
    
    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', seoData.title);
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', seoData.description);
    }
    
    // Update Twitter tags
    let twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', seoData.title);
    }
    
    let twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', seoData.description);
    }
    
    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `https://pixally.com${path === '/preview' ? '' : path.replace('/preview', '')}`);
    }
    
  }, [location]);
  
  return null;
};

export default SEOHead;
